import apiClient from './client';
import type { FileResponse, ListFilesResponse } from '../types';

// Initiate simple file upload
export interface InitiateUploadRequest {
  file_name: string;
  original_file_name?: string;
  file_size: number;
  content_type: string;
  content_hash?: string;
  estimated_duration_seconds?: number;
  preflight_transcription?: boolean;
  language?: string;
}

export interface PoPChallenge {
  token: string;
  offset: number;
  length: number;
  expires_at: string;
}

export interface InitiateUploadResponse {
  file_id: string;
  upload_url?: string;
  expires_at?: string;
  exists: boolean;
  requires_proof: boolean;
  challenge?: PoPChallenge;
}

export async function initiateUpload(data: InitiateUploadRequest): Promise<InitiateUploadResponse> {
  const response = await apiClient.post<InitiateUploadResponse>('/files/upload', data);
  return response.data;
}

export interface ClaimInstantUploadRequest {
  token: string;
  proof_sha256: string;
}

export async function claimInstantUpload(fileId: string, data: ClaimInstantUploadRequest): Promise<FileResponse> {
  const response = await apiClient.post<FileResponse>(`/files/${fileId}/claim`, data);
  return response.data;
}

export interface CheckDuplicatesRequest {
  items: Array<{ hash: string; size: number }>;
}

export interface CheckDuplicatesResponse {
  results: Array<{ hash: string; size: number; exists: boolean; instant_upload_available: boolean }>;
}

export async function checkDuplicates(data: CheckDuplicatesRequest): Promise<CheckDuplicatesResponse> {
  const response = await apiClient.post<CheckDuplicatesResponse>('/files/check-duplicate', data);
  return response.data;
}

// Initiate multipart upload for large files
export interface InitiateMultipartUploadRequest {
  file_name: string;
  original_file_name?: string;
  file_size: number;
  content_type: string;
  estimated_duration_seconds?: number;
  preflight_transcription?: boolean;
  part_size: number;  // Required, min 5MB (5242880)
  language?: string;
}

export interface InitiateMultipartUploadResponse {
  file_id: string;
  upload_id: string;
  part_size: number;
  part_count: number;
  last_part_size: number;
}

export async function initiateMultipartUpload(
  data: InitiateMultipartUploadRequest
): Promise<InitiateMultipartUploadResponse> {
  const response = await apiClient.post<InitiateMultipartUploadResponse>('/files/upload/multipart', data);
  return response.data;
}

// Get presigned URL for uploading a part
export interface PartUploadURLResponse {
  upload_url: string;
  part_number: number;
  expires_at: string;
}

export async function getPartUploadURL(fileId: string, partNumber: number): Promise<PartUploadURLResponse> {
  const response = await apiClient.get<PartUploadURLResponse>(`/files/${fileId}/upload-part/${partNumber}`);
  return response.data;
}

// Complete multipart upload
export interface CompleteMultipartUploadRequest {
  parts: Array<{
    part_number: number;
    etag: string;
  }>;
}

export async function completeMultipartUpload(
  fileId: string,
  data: CompleteMultipartUploadRequest
): Promise<void> {
  await apiClient.post(`/files/${fileId}/upload/complete`, data);
}

// Abort multipart upload
export async function abortMultipartUpload(fileId: string): Promise<void> {
  await apiClient.delete(`/files/${fileId}/upload`);
}

// Confirm upload completion
export async function confirmUpload(fileId: string): Promise<FileResponse> {
  const response = await apiClient.post<FileResponse>(`/files/${fileId}/confirm`);
  return response.data;
}

// List files with pagination and filtering
export interface ListFilesParams {
  page?: number;
  page_size?: number;
  sort?: string;
  status?: string;
  search?: string;
}

export async function listFiles(params?: ListFilesParams): Promise<ListFilesResponse> {
  const response = await apiClient.get<ListFilesResponse>('/files', { params });
  return response.data;
}

// Get file details
export async function getFile(fileId: string): Promise<FileResponse> {
  const response = await apiClient.get<FileResponse>(`/files/${fileId}`);
  return response.data;
}

// Update file metadata
export interface UpdateFileRequest {
  name?: string;
  folder_id?: string;
}

export async function updateFile(fileId: string, data: UpdateFileRequest): Promise<FileResponse> {
  const response = await apiClient.put<FileResponse>(`/files/${fileId}`, data);
  return response.data;
}

// Delete file
export async function deleteFile(fileId: string): Promise<void> {
  await apiClient.delete(`/files/${fileId}`);
}

// Get download URL
export interface DownloadURLResponse {
  download_url: string;
  expires_at: string;
  filename: string;
}

export async function getDownloadURL(fileId: string): Promise<DownloadURLResponse> {
  const response = await apiClient.get<DownloadURLResponse>(`/files/${fileId}/download`);
  return response.data;
}

// Get storage statistics
export interface StorageStatsResponse {
  total_files: number;
  total_size_bytes: number;
  files_by_status: Record<string, number>;
  files_by_type: Record<string, number>;
}

export async function getStorageStats(): Promise<StorageStatsResponse> {
  const response = await apiClient.get<StorageStatsResponse>('/files/stats');
  return response.data;
}

// Upload file directly to presigned URL (utility function)
export async function uploadToPresignedURL(
  url: string,
  file: File | Blob,
  onProgress?: (progress: number) => void,
  signal?: AbortSignal
): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let settled = false;

    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      if (signal) signal.removeEventListener('abort', onAbort);
      fn();
    };

    const onAbort = () => {
      try {
        xhr.abort();
      } catch {
        // ignore
      }
      finish(() => reject(new DOMException('Aborted', 'AbortError')));
    };

    if (signal) {
      if (signal.aborted) {
        onAbort();
        return;
      }
      signal.addEventListener('abort', onAbort);
    }

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = (event.loaded / event.total) * 100;
        onProgress(progress);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        // Get ETag from response headers for multipart uploads
        const etag = xhr.getResponseHeader('ETag') || '';
        finish(() => resolve(etag.replace(/"/g, '')));
      } else {
        finish(() => reject(new Error(`Upload failed with status ${xhr.status}`)));
      }
    });

    xhr.addEventListener('error', () => {
      finish(() => reject(new Error('Upload failed')));
    });

    xhr.addEventListener('abort', () => {
      finish(() => reject(new DOMException('Aborted', 'AbortError')));
    });

    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.send(file);
  });
}

// Move file to a different folder
export interface MoveFileRequest {
  folder_id: string | null;  // null to move to root
}

export async function moveFile(fileId: string, folderId: string | null): Promise<FileResponse> {
  const response = await apiClient.post<FileResponse>(`/files/${fileId}/move`, { folder_id: folderId });
  return response.data;
}

// Delete original media file while preserving transcription
export async function deleteOriginalFile(fileId: string): Promise<void> {
  await apiClient.delete(`/files/${fileId}/original`);
}
